import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, LoadingSpinner } from '../atoms';
import { IncompleteRequiredInventoryList, NormalFormTextField, NormalFormDateField } from '../molecules';
import { InventoryItemsTable } from './InventoryItemsTable';
import { InventoryMobileFlow } from './InventoryMobileFlow';
import { useErrorHandlerContext, usePropertyDetailContext, useUserLanguage } from '@/customHooks';
import {
  useSaveFullInspection,
  useGenerateInventoryInspectionPdf,
  useGetInventoryCatalog,
} from '@/inventoryApi';
import { downloadFileFromUrl } from '@/helpers';
import {
  DEFAULT_INVENTORY_CATALOG,
  buildDefaultSectionsForProperty,
  getCatalogItemNamesForRoomType,
  getCatalogItemsForRoomType,
  getIncompleteRequiredInventoryItems,
  formatIncompleteRequiredInventoryList,
  groupIncompleteRequiredInventoryItems,
  getSectionsMissingPhotos,
  isFinalInventoryType,
  isInventoryEditableByDate,
  isInventoryItemFilled,
  parseCatalogQuantity,
  resolveCatalogRoomTypeFromSectionKey,
  type InventoryCatalogEntry,
} from '@/helpers/inventoryCatalog';
import type {
  InventoryInspectionOutput,
  InventoryFormModalTexts,
  InventoryItemRow,
  InventoryFormSection,
  InventoryInspectionItemStatus,
  InventoryInspectionItemProvider,
  InventoryItemPresence,
  InventoryFormState,
  InventoryInspectionItem,
  InventoryInspectionSection,
  InventoryInspectionLike,
} from '@/types/custom-types';
import textsData from '@/textData/InventoryFormModalTexts.json';

type DeletedItemRef = { id: string; sectionId: string };

type InventoryFormProps = {
  mode: 'create' | 'edit';
  propertyId: string;
  inventory?: InventoryInspectionOutput | null;
  isReadOnly: boolean;
  onClose: () => void;
};

const createLocalId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `sec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeStatus = (status?: string | null): InventoryInspectionItemStatus => {
  const value = (status ?? 'NOT_EVALUATED').toUpperCase().replace('-', '_');
  if (
    value === 'EXCELLENT' ||
    value === 'GOOD' ||
    value === 'REGULAR' ||
    value === 'BAD' ||
    value === 'MISSING' ||
    value === 'NOT_EVALUATED'
  ) {
    return value;
  }
  return 'NOT_EVALUATED';
};

const normalizeProvider = (
  value?: string | null,
): InventoryInspectionItemProvider | null => {
  const normalized = (value ?? '').toUpperCase().replace(/[_-]/g, '');
  if (normalized === 'OWNER') return 'OWNER';
  if (normalized === 'LIVE4LIFE') return 'LIVE4LIFE';
  if (normalized === 'UNKNOWN' || normalized === 'UNASSIGNED') return 'UNKNOWN';
  return null;
};

const normalizePresence = (
  value?: string | boolean | null,
): InventoryItemPresence | null => {
  if (value === true || value === 'YES' || value === 'yes') return 'YES';
  if (value === false || value === 'NO' || value === 'no') return 'NO';
  if (value === 'NOT_NEEDED' || value === 'notNeeded') return 'NOT_NEEDED';
  return null;
};

const itemFromServer = (item: InventoryInspectionItem): InventoryItemRow => ({
  id: item.id,
  clientId: item.id,
  description: item.title,
  quantity: item.quantity ? Number(item.quantity) : '',
  status: normalizeStatus(item.status),
  notes: item.notes ?? '',
  isReviewed: Boolean(item.isReviewed),
  isRequired: Boolean(item.isRequired),
  isPresentInProperty: normalizePresence(item.isPresentInProperty),
  providedBy: normalizeProvider(item.providedBy),
  existingFiles: item.files ?? [],
  newFiles: [],
  shouldReplaceFiles: false,
});

const buildPreviewSections = (
  property: {
    commonBathroom?: boolean | null;
    unities?: Array<{ privateBathroom?: boolean | null }> | null;
    bedroomCount?: number | null;
    bathroomCount?: number | null;
  },
  catalog: InventoryCatalogEntry[],
): InventoryFormSection[] => {
  return buildDefaultSectionsForProperty(property).map((plan) => ({
    localId: createLocalId(),
    key: plan.key,
    title: plan.title,
    isSystem: true,
    catalogRoomType: plan.catalogRoomType,
    collapsed: false,
    items: getCatalogItemsForRoomType(plan.catalogRoomType, catalog).map((catalogItem) => ({
      clientId: createLocalId(),
      description: catalogItem.name,
      quantity: parseCatalogQuantity(catalogItem.defaultQuantity),
      status: 'NOT_EVALUATED' as InventoryInspectionItemStatus,
      notes: '',
      isReviewed: false,
      isRequired: catalogItem.isRequired,
      isPresentInProperty: null,
      providedBy: null,
      existingFiles: [],
      newFiles: [],
      shouldReplaceFiles: false,
    })),
  }));
};

const hydrateSectionsFromInventory = (
  inventory: InventoryInspectionLike,
): InventoryFormSection[] => {
  return [...inventory.sections]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((section: InventoryInspectionSection) => ({
      localId: section.id,
      id: section.id,
      key: section.key,
      title: section.title ?? section.key,
      isSystem: Boolean(section.isSystem),
      catalogRoomType: resolveCatalogRoomTypeFromSectionKey(section.key),
      collapsed: false,
      items: [...(section.items ?? [])].map(itemFromServer),
    }));
};

export const InventoryForm = ({
  mode,
  propertyId,
  inventory,
  isReadOnly: isReadOnlyProp,
  onClose,
}: InventoryFormProps) => {
  const userLanguage = useUserLanguage();
  const texts: InventoryFormModalTexts = userLanguage === 'es' ? textsData.es : textsData.en;

  const { open: errorHandle } = useErrorHandlerContext();
  const { property } = usePropertyDetailContext();

  const editLockedByDate =
    mode === 'edit' &&
    Boolean(inventory?.inspectionDate) &&
    !isInventoryEditableByDate(inventory?.inspectionDate);
  const isReadOnly = isReadOnlyProp || editLockedByDate;

  const { saveFullInspection, loading: isSaving } = useSaveFullInspection();
  const { generate: generatePdf, loading: isGeneratingPdf } = useGenerateInventoryInspectionPdf({
    onError: errorHandle,
  });
  const { data: catalogData } = useGetInventoryCatalog({ skip: false });

  const catalog: InventoryCatalogEntry[] = useMemo(() => {
    if (!catalogData?.length) return DEFAULT_INVENTORY_CATALOG;
    return catalogData.map((entry) => ({
      roomType: entry.roomType as InventoryCatalogEntry['roomType'],
      items: (entry.items ?? []).map((item) => ({
        name: item.name,
        isRequired: Boolean(item.isRequired),
        defaultQuantity: item.defaultQuantity ?? undefined,
      })),
    }));
  }, [catalogData]);

  const [formState, setFormState] = useState<InventoryFormState>({
    description: '',
    inspectionType: 'INITIAL',
    inspectionDate: '',
    inspectorName: '',
    receiverName: '',
    generalNotes: '',
  });
  const [formSections, setFormSections] = useState<InventoryFormSection[]>([]);
  const [deletedSectionIds, setDeletedSectionIds] = useState<string[]>([]);
  const [deletedItemIds, setDeletedItemIds] = useState<DeletedItemRef[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveProgress, setSaveProgress] = useState<{ done: number; total: number } | null>(null);
  const [showAbandonModal, setShowAbandonModal] = useState(false);
  const isSubmittingRef = useRef(false);

  const [isDesktopView, setIsDesktopView] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktopView(event.matches);
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const progress = useMemo(() => {
    const allItems = formSections.flatMap((section) => section.items);
    const done = allItems.filter((item) => isInventoryItemFilled(item)).length;
    return { done, total: allItems.length };
  }, [formSections]);

  const progressPercent = progress.total ? (progress.done / progress.total) * 100 : 0;
  const incompleteRequiredItems = useMemo(
    () => getIncompleteRequiredInventoryItems(formSections),
    [formSections],
  );
  const incompleteRequiredLabels = useMemo(
    () => formatIncompleteRequiredInventoryList(formSections),
    [formSections],
  );
  const incompleteRequiredGroups = useMemo(
    () => groupIncompleteRequiredInventoryItems(formSections),
    [formSections],
  );
  const sectionsMissingPhotos = useMemo(
    () => getSectionsMissingPhotos(formSections),
    [formSections],
  );
  const canCompleteInventory =
    incompleteRequiredItems.length === 0 && sectionsMissingPhotos.length === 0;
  const hasInspectorName = formState.inspectorName.trim().length > 0;
  const canSaveInventory = hasInspectorName;
  const incompleteRequiredMessage =
    incompleteRequiredLabels.length > 0
      ? `${texts.incompleteRequired}\n${incompleteRequiredLabels
          .slice(0, 5)
          .map((label) => `- ${label}`)
          .join('\n')}${
          incompleteRequiredLabels.length > 5
            ? `\n… (+${incompleteRequiredLabels.length - 5})`
            : ''
        }`
      : texts.incompleteRequired;
  const showTemplateBanner = mode === 'create' || formSections.some((section) => section.isSystem);

  const handleFormFieldChange = <K extends keyof InventoryFormState>(
    field: K,
    value: InventoryFormState[K],
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  /* eslint-disable react-hooks/set-state-in-effect -- form hydration from props */
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);

    if ((mode === 'edit' || isReadOnlyProp) && inventory) {
      const editableInventory = inventory as InventoryInspectionLike;
      setFormState({
        description: '',
        inspectionType: isFinalInventoryType(editableInventory.type) ? 'FINAL' : 'INITIAL',
        inspectionDate: editableInventory.inspectionDate.slice(0, 10),
        inspectorName: editableInventory.inspectorName ?? '',
        receiverName: editableInventory.receiverName ?? '',
        generalNotes: editableInventory.generalNotes ?? '',
      });
      setFormSections(hydrateSectionsFromInventory(editableInventory));
      setDeletedSectionIds([]);
      setDeletedItemIds([]);
      return;
    }

    setFormState({
      description: '',
      inspectionType: 'INITIAL',
      inspectionDate: today,
      inspectorName: '',
      receiverName: '',
      generalNotes: '',
    });
    setFormSections(
      buildPreviewSections(
        {
          commonBathroom: property?.commonBathroom,
          unities: property?.unities,
          bedroomCount: property?.bedroomCount,
          bathroomCount: property?.bathroomCount,
        },
        catalog,
      ),
    );
    setDeletedSectionIds([]);
    setDeletedItemIds([]);
  }, [
    mode,
    inventory,
    isReadOnlyProp,
    property?.id,
    property?.commonBathroom,
    property?.unities,
    property?.bedroomCount,
    property?.bathroomCount,
  ]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const updateSection = (localId: string, patch: Partial<InventoryFormSection>) => {
    setFormSections((prev) =>
      prev.map((section) => (section.localId === localId ? { ...section, ...patch } : section)),
    );
  };

  const handleAddPreparedSection = (section: InventoryFormSection) => {
    if (isReadOnly) return;
    setFormSections((prev) => [...prev, section]);
  };

  const handleAddSection = () => {
    if (isReadOnly) return;
    const order = formSections.length;
    handleAddPreparedSection({
      localId: createLocalId(),
      key: `custom-${createLocalId()}`,
      title: userLanguage === 'es' ? `Estancia ${order + 1}` : `Room ${order + 1}`,
      isSystem: false,
      catalogRoomType: null,
      collapsed: false,
      items: [],
    });
  };

  const handleRemoveSection = (section: InventoryFormSection) => {
    if (isReadOnly) return;
    if (section.isSystem || section.items.some((item) => item.isRequired)) return;
    if (section.id) {
      setDeletedSectionIds((prev) => [...prev, section.id as string]);
      section.items.forEach((item) => {
        if (item.id) {
          setDeletedItemIds((prev) => [
            ...prev,
            { id: item.id as string, sectionId: section.id as string },
          ]);
        }
      });
    }
    setFormSections((prev) => prev.filter((s) => s.localId !== section.localId));
  };

  const setSectionItems = (localId: string, items: InventoryItemRow[]) => {
    setFormSections((prev) =>
      prev.map((section) => {
        if (section.localId !== localId) return section;
        const removed = section.items.filter(
          (prevItem) => prevItem.id && !items.some((item) => item.clientId === prevItem.clientId),
        );
        if (removed.length > 0 && section.id) {
          setDeletedItemIds((ids) => [
            ...ids,
            ...removed
              .filter((item) => item.id)
              .map((item) => ({
                id: item.id as string,
                sectionId: section.id as string,
              })),
          ]);
        }
        return { ...section, items };
      }),
    );
  };

  const expandAll = (open: boolean) => {
    setFormSections((prev) => prev.map((section) => ({ ...section, collapsed: !open })));
  };

  const persistInventory = async (options: { asDraft: boolean }) => {
    const inspectorName = formState.inspectorName.trim();
    if (!inspectorName) {
      throw new Error(texts.inspectorRequired);
    }

    setIsSubmitting(true);
    setSaveProgress({
      done: 0,
      total: formSections.reduce((sum, section) => sum + section.items.length, 0),
    });

    await saveFullInspection({
      propertyId,
      mode,
      formState: { ...formState, inspectorName },
      formSections,
      inventory: mode === 'edit' ? inventory : undefined,
      asDraft: options.asDraft,
      deletedSectionIds,
      deletedItemIds,
      onProgress: (done, total) => setSaveProgress({ done, total }),
    });

    setShowAbandonModal(false);
    onClose();
  };

  const handleClose = () => {
    if (isSubmittingRef.current || isSubmitting || isSaving) return;
    if (isReadOnly) {
      onClose();
      return;
    }
    setShowAbandonModal(true);
  };

  const handleAbandon = () => {
    if (isSubmittingRef.current || isSubmitting) return;
    setShowAbandonModal(false);
    onClose();
  };

  const handleSavePending = async () => {
    if (isSubmittingRef.current || isSubmitting) return;
    isSubmittingRef.current = true;
    try {
      await persistInventory({ asDraft: true });
    } catch (error) {
      errorHandle(error);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      setSaveProgress(null);
    }
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current || isSubmitting) return;
    if (!formState.inspectorName.trim()) {
      errorHandle(new Error(texts.inspectorRequired));
      return;
    }

    isSubmittingRef.current = true;
    try {
      await persistInventory({ asDraft: !canCompleteInventory });
    } catch (error) {
      errorHandle(error);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      setSaveProgress(null);
    }
  };

  const title = isReadOnly
    ? texts.titleDetails
    : mode === 'create'
      ? texts.titleNew
      : texts.titleEdit;

  const canDownloadPdf = Boolean(propertyId && inventory?.id);

  const handleDownloadPdf = async () => {
    try {
      if (!propertyId || !inventory?.id) return;
      const payload = await generatePdf(propertyId, inventory.id);
      if (!payload?.url) return;
      await downloadFileFromUrl(payload.url, payload.fileName ?? 'inspection.pdf');
    } catch (e) {
      errorHandle(e);
    }
  };

  const bedroomCount =
    property?.unities?.length ??
    property?.bedroomCount ??
    0;
  const bathroomCountFromFlags =
    (property?.commonBathroom ? 1 : 0) +
    (property?.unities ?? []).filter((unity) => unity.privateBathroom).length;
  const bathroomCount =
    bathroomCountFromFlags > 0 ? bathroomCountFromFlags : (property?.bathroomCount ?? 0);

  const busy = isSubmitting || isSaving;

  return (
    <div className="relative">
      {busy && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/85 px-6 text-center backdrop-blur-[1px]">
          <LoadingSpinner />
          <div>
            <p className="text-base font-semibold text-slate-800">{texts.savingTitle}</p>
            <p className="mt-1 max-w-sm text-sm text-slate-500">{texts.savingBody}</p>
            {saveProgress && saveProgress.total > 0 ? (
              <p className="mt-3 text-sm font-medium text-[#3c7c51]">
                {texts.savingProgress}: {saveProgress.done} {texts.progressOf}{' '}
                {saveProgress.total} {texts.itemsFromCatalog}
              </p>
            ) : null}
            {saveProgress && saveProgress.total > 0 ? (
              <span className="mt-3 inline-block h-2 w-48 overflow-hidden rounded-md bg-slate-200 align-middle">
                <span
                  className="block h-full bg-[#4A9462] transition-[width] duration-200"
                  style={{
                    width: `${Math.min(100, (saveProgress.done / saveProgress.total) * 100)}%`,
                  }}
                />
              </span>
            ) : null}
          </div>
        </div>
      )}

      {!isDesktopView ? (
        <>
          {editLockedByDate ? (
            <div className="mx-4 mb-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
              {texts.editLockedByDate}
            </div>
          ) : null}
          <InventoryMobileFlow
            title={title}
            texts={texts}
            formState={formState}
            formSections={formSections}
            catalog={catalog}
            property={property ?? undefined}
            isReadOnly={isReadOnly}
            canSaveInventory={canSaveInventory}
            incompleteRequiredGroups={incompleteRequiredGroups}
            saveBlockedDetail={
              !canCompleteInventory
                ? incompleteRequiredLabels.length > 0
                  ? incompleteRequiredMessage
                  : sectionsMissingPhotos.length > 0
                    ? texts.incompletePhotos.replace(
                        '{rooms}',
                        sectionsMissingPhotos.map((section) => section.title).join(', '),
                      )
                    : texts.saveBlockedHint
                : null
            }
            isSubmitting={busy}
            onFormFieldChange={handleFormFieldChange}
            onSetSectionItems={setSectionItems}
            onAddSection={handleAddPreparedSection}
            onSubmit={handleSubmit}
            onClose={handleClose}
          />
        </>
      ) : (
        <div className="relative max-h-[85vh] space-y-5 overflow-y-auto pr-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-slate-800">{title}</h2>
              {property && (
                <p className="mt-1 text-sm text-slate-500">
                  {property.address?.street ?? ''} {property.address?.buildingNumber ?? ''} ·{' '}
                  {property.address?.city?.name ?? ''}
                  {bedroomCount > 0 || bathroomCount > 0
                    ? ` · ${bedroomCount} dorm. · ${bathroomCount} baño${bathroomCount === 1 ? '' : 's'}`
                    : ''}
                </p>
              )}
            </div>
            {showTemplateBanner && (
              <span className="rounded-full bg-[#eef5f0] px-3 py-1 text-[10.5px] font-semibold text-[#3c7c51]">
                ✓ {texts.badgeTemplate}
              </span>
            )}
          </div>

          {editLockedByDate ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
              {texts.editLockedByDate}
            </div>
          ) : null}

          {showTemplateBanner && (
            <div className="flex items-start gap-3 rounded-xl border border-[#cfe4d7] bg-[#eef5f0] px-4 py-3 text-sm text-[#3c7c51]">
              <div>
                <b className="text-slate-800">{texts.bannerTitle}</b> {texts.bannerBody}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)_minmax(0,1.2fr)]">
              <div>
                <p className="mb-2 font-semibold">Tipo de inspección</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    secondary={formState.inspectionType !== 'INITIAL'}
                    onClick={() => !isReadOnly && handleFormFieldChange('inspectionType', 'INITIAL')}
                  >
                    Entrega inicial
                  </Button>
                  <Button
                    secondary={formState.inspectionType !== 'FINAL'}
                    onClick={() => !isReadOnly && handleFormFieldChange('inspectionType', 'FINAL')}
                  >
                    Retorno / Final
                  </Button>
                </div>
              </div>
              <div>
                <NormalFormDateField
                  label="Fecha"
                  name="inspectionDate"
                  onChange={(value) => handleFormFieldChange('inspectionDate', value ?? '')}
                  required
                  value={formState.inspectionDate}
                  readOnly={isReadOnly}
                />
              </div>
              <div>
                <NormalFormTextField
                  label="Dirección"
                  name="inspectionAddress"
                  value={
                    property
                      ? `${property.address?.street ?? ''} ${property.address?.buildingNumber ?? ''} ${
                          property.address?.city?.name ?? ''
                        }`
                      : ''
                  }
                  readOnly
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <NormalFormTextField
                label={texts.labels.inspector}
                name="inspectorName"
                onChange={(value) => handleFormFieldChange('inspectorName', value ?? '')}
                value={formState.inspectorName}
                readOnly={isReadOnly}
              />
              <NormalFormTextField
                label="Receptor / Recibe"
                name="receiverName"
                onChange={(value) => handleFormFieldChange('receiverName', value ?? '')}
                value={formState.receiverName}
                readOnly={isReadOnly}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
              <span>
                {texts.progressLabel}:{' '}
                <b className="text-[#3c7c51]">
                  {progress.done} {texts.progressOf} {progress.total}
                </b>{' '}
                {texts.itemsFromCatalog}
              </span>
              <span className="inline-block h-2 w-40 overflow-hidden rounded-md bg-slate-200 align-middle">
                <span
                  className="block h-full bg-[#4A9462] transition-[width] duration-200"
                  style={{ width: `${progressPercent}%` }}
                />
              </span>
            </div>
            <div className="flex gap-2">
              <Button secondary onClick={() => expandAll(true)}>
                {texts.expandAll}
              </Button>
              <Button secondary onClick={() => expandAll(false)}>
                {texts.collapseAll}
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            {formSections.map((section) => (
              <div
                key={section.localId}
                className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
              >
                <div className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    onClick={() => updateSection(section.localId, { collapsed: !section.collapsed })}
                  >
                    <span
                      className={`text-slate-400 transition-transform ${section.collapsed ? '-rotate-90' : ''}`}
                    >
                      ▾
                    </span>
                    {isReadOnly ? (
                      <span className="truncate font-semibold text-slate-800">{section.title}</span>
                    ) : (
                      <input
                        type="text"
                        className="w-full min-w-0 truncate rounded-md border border-transparent bg-transparent px-1 py-0.5 font-semibold text-slate-800 outline-none hover:border-slate-200 focus:border-[#4A9462]"
                        value={section.title}
                        placeholder={texts.sectionTitlePlaceholder}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) =>
                          updateSection(section.localId, { title: event.target.value })
                        }
                      />
                    )}
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap text-xs text-slate-400">
                      {section.items.length} {texts.itemsFromCatalog}
                    </span>
                    {!isReadOnly &&
                      !section.isSystem &&
                      !section.items.some((item) => item.isRequired) && (
                        <button
                          type="button"
                          className="rounded-lg px-2 py-1 text-xs text-slate-400 hover:bg-red-50 hover:text-red-500"
                          onClick={() => handleRemoveSection(section)}
                        >
                          {texts.removeSection}
                        </button>
                      )}
                  </div>
                </div>
                {!section.collapsed && (
                  <div className="px-3 pb-3">
                    <InventoryItemsTable
                      items={section.items}
                      onChange={(items) => setSectionItems(section.localId, items)}
                      texts={texts}
                      isReadOnly={isReadOnly}
                      catalogSuggestions={getCatalogItemNamesForRoomType(
                        section.catalogRoomType,
                        catalog,
                      )}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {!isReadOnly && (
            <button
              type="button"
              onClick={handleAddSection}
              className="rounded-lg border border-dashed border-[#bcd6c6] px-3 py-2 text-sm font-semibold text-[#3c7c51] hover:bg-[#eef5f0]"
            >
              ＋ {texts.addSection}
            </button>
          )}

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <label className="text-sm font-semibold text-slate-800">{texts.generalNotes}</label>
            <textarea
              className="mt-2 min-h-[70px] w-full resize-y rounded-[10px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#4A9462]"
              placeholder={texts.generalNotesPlaceholder}
              value={formState.generalNotes}
              readOnly={isReadOnly}
              onChange={(event) => handleFormFieldChange('generalNotes', event.target.value)}
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Button secondary onClick={handleClose} disabled={busy}>
                {texts.buttons.back}
              </Button>
              {isReadOnly && canDownloadPdf && (
                <Button secondary onClick={handleDownloadPdf} isLoading={isGeneratingPdf}>
                  {texts.generatePdf}
                </Button>
              )}
            </div>
            {!isReadOnly && (
              <div className="flex flex-col items-end gap-2">
                {!canCompleteInventory && (
                  <div className="max-w-sm text-left text-xs font-medium text-amber-700">
                    {incompleteRequiredGroups.length > 0 ? (
                      <IncompleteRequiredInventoryList
                        title={texts.incompleteRequired}
                        groups={incompleteRequiredGroups}
                        seeMoreLabel={texts.seeMoreIncomplete}
                      />
                    ) : (
                      <p className="m-0">{texts.saveBlockedHint}</p>
                    )}
                  </div>
                )}
                <div className="flex gap-2">
                  {mode === 'edit' && canDownloadPdf && (
                    <Button
                      secondary
                      onClick={handleDownloadPdf}
                      isLoading={isGeneratingPdf}
                      disabled={busy}
                    >
                      {texts.generatePdf}
                    </Button>
                  )}
                  <Button
                    onClick={handleSubmit}
                    isLoading={busy}
                    disabled={busy || !canSaveInventory}
                  >
                    {texts.saveInventory}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showAbandonModal ? (
        <div className="fixed inset-0 z-30 flex items-end justify-center bg-black/45 p-4 sm:items-center">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="inventory-abandon-title"
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
          >
            <h2
              id="inventory-abandon-title"
              className="m-0 text-lg font-extrabold text-[#16283D]"
            >
              {texts.abandonTitle}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-[#5A6B7D]">{texts.abandonBody}</p>
            <div className="mt-5 flex flex-col gap-2.5">
              <button
                type="button"
                disabled={busy}
                className="flex min-h-12 w-full items-center justify-center rounded-[14px] bg-[#0B3465] text-[15px] font-bold text-white disabled:opacity-50"
                onClick={handleSavePending}
              >
                {texts.abandonSavePending}
              </button>
              <button
                type="button"
                disabled={busy}
                className="flex min-h-12 w-full items-center justify-center rounded-[14px] bg-[#C0504A] text-[15px] font-bold text-white disabled:opacity-50"
                onClick={handleAbandon}
              >
                {texts.abandonConfirm}
              </button>
              <button
                type="button"
                disabled={busy}
                className="flex min-h-11 w-full items-center justify-center rounded-[14px] border border-[#E7ECF1] bg-white text-[14px] font-semibold text-[#5A6B7D] disabled:opacity-50"
                onClick={() => setShowAbandonModal(false)}
              >
                {texts.abandonKeepEditing}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
